import { useEffect, useRef } from "react";
import type { Polyline } from "@tbm/shared";
import type { DrawingPayload } from "./loadDrawing";
import { chainageTicks, interpolateAt } from "./PolylineMath";

export interface PolylineLayer {
  points: Polyline;
  /** Stroke colour for the polyline. */
  color: string;
  /** TBM marker fraction (0..1) along the polyline, or null to hide. */
  markerFraction: number | null;
  /** Drive length used for tick chainage labels. */
  driveLengthM: number | null;
}

interface Props {
  payload: DrawingPayload | null;
  layers: PolylineLayer[];
  onClick?: (point: [number, number]) => void;
  cursorMode?: "view" | "edit";
}

/**
 * Renders the drawing as a hardware-accelerated `<img>` (so PDFs and
 * images downscale crisply) with a transparent overlay canvas on top for
 * the polyline alignments, chainage ticks, and TBM markers.
 */
export function DrawingCanvas({
  payload,
  layers,
  onClick,
  cursorMode = "view",
}: Props) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const imgRectRef = useRef<{ x: number; y: number; w: number; h: number } | null>(null);
  const imgElRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return;

    const draw = () => {
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const dpr = window.devicePixelRatio || 1;
      const cw = wrap.clientWidth;
      const ch = wrap.clientHeight;
      canvas.width = Math.max(1, Math.floor(cw * dpr));
      canvas.height = Math.max(1, Math.floor(ch * dpr));
      canvas.style.width = `${cw}px`;
      canvas.style.height = `${ch}px`;
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, cw, ch);

      if (!payload) {
        imgRectRef.current = null;
        return;
      }

      const sw = payload.naturalWidth;
      const sh = payload.naturalHeight;
      if (!sw || !sh) return;
      const scale = Math.min(cw / sw, ch / sh);
      const dw = sw * scale;
      const dh = sh * scale;
      const dx = (cw - dw) / 2;
      const dy = (ch - dh) / 2;
      imgRectRef.current = { x: dx, y: dy, w: dw, h: dh };

      if (imgElRef.current) {
        imgElRef.current.style.left = `${dx}px`;
        imgElRef.current.style.top = `${dy}px`;
        imgElRef.current.style.width = `${dw}px`;
        imgElRef.current.style.height = `${dh}px`;
      }

      for (const layer of layers) {
        const pts = layer.points;
        if (pts.length === 0) continue;

        // White halo under the polyline so it stays legible on dense drawings.
        ctx.lineJoin = "round";
        ctx.lineCap = "round";
        ctx.strokeStyle = "rgba(255,255,255,0.85)";
        ctx.lineWidth = 2.4;
        ctx.beginPath();
        for (let i = 0; i < pts.length; i++) {
          const px = dx + pts[i]![0] * dw;
          const py = dy + pts[i]![1] * dh;
          if (i === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        }
        ctx.stroke();

        // The polyline itself — thin precise stroke.
        ctx.strokeStyle = layer.color;
        ctx.lineWidth = 1.1;
        ctx.beginPath();
        for (let i = 0; i < pts.length; i++) {
          const px = dx + pts[i]![0] * dw;
          const py = dy + pts[i]![1] * dh;
          if (i === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        }
        ctx.stroke();

        // Chainage ticks every 20m. Major ticks (every 100m) get a longer
        // stroke + a centred chainage label sitting alongside the line.
        if (pts.length >= 2 && layer.driveLengthM != null && layer.driveLengthM > 0) {
          const ticks = chainageTicks(pts, layer.driveLengthM, 20);
          ctx.font = "9px Helvetica, Arial, sans-serif";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";

          for (const tk of ticks) {
            const px = dx + tk.x * dw;
            const py = dy + tk.y * dh;
            const nx = -Math.sin(tk.angle);
            const ny = Math.cos(tk.angle);
            const isMajor = Math.round(tk.chainageM) % 100 === 0;

            const len = isMajor ? 5 : 3;
            ctx.strokeStyle = withAlpha(layer.color, isMajor ? 0.7 : 0.35);
            ctx.lineWidth = isMajor ? 0.9 : 0.7;
            ctx.beginPath();
            ctx.moveTo(px + nx * len, py + ny * len);
            ctx.lineTo(px - nx * len, py - ny * len);
            ctx.stroke();

            if (isMajor) {
              const offset = len + 9;
              const lx = px + nx * offset;
              const ly = py + ny * offset;
              const lbl = `${Math.round(tk.chainageM)}`;
              // White halo behind text so it reads on busy drawings.
              ctx.lineWidth = 3;
              ctx.strokeStyle = "rgba(255,255,255,0.9)";
              ctx.strokeText(lbl, lx, ly);
              ctx.fillStyle = "#374151";
              ctx.fillText(lbl, lx, ly);
            }
          }
        }

        // TBM marker — small ring with a thin white halo and a filled core.
        // No text label.
        if (layer.markerFraction != null && pts.length >= 2) {
          const sample = interpolateAt(pts, layer.markerFraction);
          if (sample) {
            const mx = dx + sample.x * dw;
            const my = dy + sample.y * dh;
            // White contrast halo
            ctx.strokeStyle = "rgba(255,255,255,0.95)";
            ctx.lineWidth = 2.2;
            ctx.beginPath();
            ctx.arc(mx, my, 4.5, 0, 2 * Math.PI);
            ctx.stroke();
            // Coloured ring
            ctx.strokeStyle = layer.color;
            ctx.lineWidth = 1.1;
            ctx.beginPath();
            ctx.arc(mx, my, 4.5, 0, 2 * Math.PI);
            ctx.stroke();
            // Filled core
            ctx.fillStyle = layer.color;
            ctx.beginPath();
            ctx.arc(mx, my, 1.8, 0, 2 * Math.PI);
            ctx.fill();
          }
        }
      }
    };

    draw();
    const ro = new ResizeObserver(draw);
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [payload, layers]);

  function handleClick(e: React.MouseEvent<HTMLCanvasElement>) {
    if (!onClick || !imgRectRef.current) return;
    const r = canvasRef.current!.getBoundingClientRect();
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;
    const ir = imgRectRef.current;
    if (x < ir.x || x > ir.x + ir.w || y < ir.y || y > ir.y + ir.h) return;
    const nx = (x - ir.x) / ir.w;
    const ny = (y - ir.y) / ir.h;
    onClick([nx, ny]);
  }

  return (
    <div
      ref={wrapRef}
      className="relative w-full h-full bg-white"
      style={{ cursor: cursorMode === "edit" ? "crosshair" : "default" }}
    >
      {payload && (
        <img
          ref={imgElRef}
          src={payload.imageUrl}
          alt="Drawing"
          draggable={false}
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            width: 0,
            height: 0,
            pointerEvents: "none",
            userSelect: "none",
          }}
        />
      )}
      <canvas
        ref={canvasRef}
        onClick={handleClick}
        className="absolute inset-0"
      />
    </div>
  );
}

function withAlpha(hex: string, alpha: number): string {
  let r = 0, g = 0, b = 0;
  if (hex.startsWith("#") && (hex.length === 7 || hex.length === 9)) {
    r = parseInt(hex.slice(1, 3), 16);
    g = parseInt(hex.slice(3, 5), 16);
    b = parseInt(hex.slice(5, 7), 16);
  }
  return `rgba(${r},${g},${b},${alpha})`;
}
